/*----------------------------------
- COMPONENT: PUBLIC DOMAINS ROW CARDS
----------------------------------*/

/**
 * @docs docs/features/website-domains-hubs
 * @adr ADR-0028
 *
 * The phone presentation of the bounded public preview (the mock's `.dm-cards`, website-domains.html).
 *
 * Same rows, same order, same cap as the table: this component receives the rows the page has already resolved
 * and draws them, so there is no second query, no pagination and no client-side ordering. The handoff under them
 * (an ink card over three text-free ghost rows) is the table's See all in the phone's shape, with the same handler
 * and the same test id, so the conversion path is one path.
 */

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import { Button } from '@uniquedomains/components/kit/button/Button';
import { BROWSE_ESTIMATE_SPOKEN_LABEL } from '@uniquedomains/components/radar/catalogs/browseResultCountCopy';
import {
    PUBLIC_PREVIEW_HANDOFF_COPY,
    PUBLIC_PREVIEW_TABLE_COPY,
    fillPreviewCopy,
} from '@/client/catalogs/landing/domains/publicPreviewCopy';

import PublicDomainsRowCard from './PublicDomainsRowCard';

// Types
import type { PublicDomainsTableColumn } from '@/client/catalogs/landing/domains/publicTableColumns';
import type { PublicDomainsTableRow } from '../table/PublicDomainsTable';
import type { DatabaseBrowseCountPrecision } from '@uniquedomains/components/contracts/domains/browseFacets';

import '../table/domainsTable.css';

/*----------------------------------
- CONSTANTS
----------------------------------*/

/**
 * The columns the card's first line already draws (status, price, renewal) and the rating, which leads the bands
 * as "Our read". Every other catalog column becomes a band, so the card cannot silently drop a column the table
 * gained.
 */
const CARD_HEAD_COLUMN_IDS: readonly string[] = ['acquisition', 'askPrice', 'renewal', 'verdict'];

/** Ghost rows behind the handoff block: decoration for the rows the preview stops at. */
const GHOST_ROW_KEYS = ['a', 'b', 'c'] as const;

/*----------------------------------
- TYPES
----------------------------------*/

type PublicDomainsRowCardsProps = {
    rows: readonly PublicDomainsTableRow[];
    columns: readonly PublicDomainsTableColumn[];
    totalCountLabel: string | null;
    /** Adds the spoken "(estimated)" after a sampled total (ADR-0034 section 5). */
    countPrecision: DatabaseBrowseCountPrecision;
    /** Every gated affordance hands off through the same conversion path the table uses. */
    onOpenRow: (domain: string) => void;
    onSeeAll: () => void;
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const PublicDomainsRowCards = ({ rows, columns, totalCountLabel, countPrecision, onOpenRow, onSeeAll }: PublicDomainsRowCardsProps) => {
    const bandColumns = columns.filter((column) => !CARD_HEAD_COLUMN_IDS.includes(column.id));
    const rowCount = rows.length.toLocaleString('en-US');
    const copy = PUBLIC_PREVIEW_TABLE_COPY;

    return (
        <div class="dm-cards">
            <p class="dm-showing">
                {copy.showing} <b>{rowCount}</b>
                {totalCountLabel ? (
                    <>
                        {' '}
                        {copy.showingOf} <b>{totalCountLabel}</b>
                        {countPrecision === 'sampled' ? <span class="ud-sr-only"> {BROWSE_ESTIMATE_SPOKEN_LABEL}</span> : null}
                    </>
                ) : null}
            </p>

            <div class="dm-cardlist">
                {rows.map(({ key, row, projection }) => (
                    <PublicDomainsRowCard key={key} row={row} projection={projection} bandColumns={bandColumns} onOpen={onOpenRow} />
                ))}
            </div>

            <div class="dm-handoff">
                <div class="dm-handoff__ghosts" aria-hidden="true">
                    {GHOST_ROW_KEYS.map((ghostKey) => <span key={ghostKey} />)}
                </div>
                <div class="dm-handoff__card">
                    <span class="wp-lab">{fillPreviewCopy(PUBLIC_PREVIEW_HANDOFF_COPY.eyebrow, { count: rowCount })}</span>
                    <p class="dm-handoff__t">
                        {totalCountLabel
                            ? fillPreviewCopy(PUBLIC_PREVIEW_HANDOFF_COPY.title, { total: totalCountLabel })
                            : PUBLIC_PREVIEW_HANDOFF_COPY.titleFallback}
                    </p>
                    <p class="dm-handoff__b">{fillPreviewCopy(PUBLIC_PREVIEW_HANDOFF_COPY.body, { count: rowCount })}</p>
                    <Button variant="soft" size="md" cta block data-testid="domains-see-all" onClick={onSeeAll}>
                        {PUBLIC_PREVIEW_HANDOFF_COPY.cta}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PublicDomainsRowCards;
